import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, Check, Gift, User as UserIcon } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import API from "../services/api";
import BrandLogo from "../components/BrandLogo";
import { resetFirebasePhoneOtp, sendFirebasePhoneOtp, verifyFirebasePhoneOtp } from "../services/firebaseAuth";
import { isAppWebView } from "../utils/appWebView";

const getErrorMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;
const getFirebaseOtpMessage = (err) => {
  const code = err?.code || "";
  if (code.includes("too-many-requests")) return "Too many OTP requests. Please try again later.";
  if (code.includes("invalid-phone-number")) return "Enter valid 10 digit mobile number.";
  if (code.includes("captcha-check-failed")) return "OTP security check failed. Please try again.";
  if (code.includes("unauthorized-domain")) return "OTP domain is not authorized.";
  if (code.includes("app-not-authorized")) return "OTP app is not authorized.";
  if (err?.message === "Request timed out") return "OTP request timed out. Please try again.";
  return "Unable to send OTP. Please try again.";
};

const toTenDigitPhone = (value) => String(value || "").replace(/\D/g, "").slice(0, 10);
const OTP_EXPIRY_SECONDS = 90;
const withTimeout = (promise, ms) =>
  Promise.race([
    promise,
    new Promise((_, reject) => window.setTimeout(() => reject(new Error("OTP request timed out")), ms)),
  ]);

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState("login");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [referral, setReferral] = useState("");
  const [otp, setOtp] = useState("");
  const [loginCode, setLoginCode] = useState("");
  const [showCodeLogin, setShowCodeLogin] = useState(false);
  const [step, setStep] = useState(1);
  const [notice, setNotice] = useState("");
  const [firebaseOtp, setFirebaseOtp] = useState(null);
  const [otpProvider, setOtpProvider] = useState("");
  const [otpExpiresAt, setOtpExpiresAt] = useState(0);
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);
  const noticeTimerRef = useRef(null);
  const inAppWebView = isAppWebView();

  const showNotice = (message) => {
    setNotice(message);
    window.clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = window.setTimeout(() => setNotice(""), 3200);
  };

  const resetFlow = (nextTab) => {
    setTab(nextTab);
    setStep(1);
    setOtp("");
    setLoginCode("");
    setShowCodeLogin(false);
    setFirebaseOtp(null);
    setOtpProvider("");
    setOtpExpiresAt(0);
    setOtpSecondsLeft(0);
    setActionLoading(false);
    setNotice("");
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nextTab = String(params.get("tab") || "").toLowerCase() === "register" ? "register" : "login";
    const nextReferral = String(params.get("ref") || "").trim();

    setTab(nextTab);
    setStep(1);
    setOtp("");
    setLoginCode("");
    setShowCodeLogin(false);
    setFirebaseOtp(null);
    setOtpProvider("");
    setOtpExpiresAt(0);
    setOtpSecondsLeft(0);
    setActionLoading(false);
    setNotice("");
    setReferral(nextReferral);
  }, [location.search]);

  useEffect(() => {
    if (!otpExpiresAt) return undefined;

    const updateRemaining = () => {
      const next = Math.max(0, Math.ceil((otpExpiresAt - Date.now()) / 1000));
      setOtpSecondsLeft(next);
      if (next === 0) {
        setFirebaseOtp(null);
        resetFirebasePhoneOtp();
      }
    };

    updateRemaining();
    const timer = window.setInterval(updateRemaining, 250);
    return () => window.clearInterval(timer);
  }, [otpExpiresAt]);

  const sendOtp = async () => {
    if (actionLoading) return;
    const cleanPhone = toTenDigitPhone(phone);
    if (cleanPhone.length !== 10) {
      showNotice("Enter valid 10 digit mobile number");
      return;
    }

    setActionLoading(true);
    try {
      const existsRes = await API.get(`/users/exists/${cleanPhone}`);
      const userExists = Boolean(existsRes.data?.exists);

      if (tab === "login" && !userExists) {
        resetFlow("register");
        setPhone(cleanPhone);
        showNotice("Number not registered. Please register.");
        return;
      }

      if (tab === "register" && userExists) {
        resetFlow("login");
        setPhone(cleanPhone);
        showNotice("Number already registered. Please login.");
        return;
      }

      try {
        await API.post("/firebase-otp-session", { phone: cleanPhone, mode: tab }).catch(() => {});
        const confirmation = await withTimeout(sendFirebasePhoneOtp(cleanPhone), 30000);
        setFirebaseOtp(confirmation);
        setOtpProvider("firebase");
        setPhone(cleanPhone);
        setStep(2);
        setOtp("");
        const expiresAt = Date.now() + OTP_EXPIRY_SECONDS * 1000;
        setOtpExpiresAt(expiresAt);
        setOtpSecondsLeft(OTP_EXPIRY_SECONDS);
        showNotice("OTP sent successfully");
        return;
      } catch (firebaseErr) {
        resetFirebasePhoneOtp();
        console.log("OTP error:", firebaseErr?.code || firebaseErr?.message || firebaseErr);
        setFirebaseOtp(null);
        showNotice(getFirebaseOtpMessage(firebaseErr));
        return;
      }
    } catch (err) {
      if (err?.response?.data?.code === "USER_NOT_FOUND") {
        resetFlow("register");
        showNotice("New user register first");
        return;
      }
      showNotice(getErrorMessage(err, "Error sending OTP"));
    } finally {
      setActionLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (actionLoading) return;
    try {
      if (!firebaseOtp && otpProvider !== "sms") {
        showNotice("Please request OTP again");
        return;
      }

      setActionLoading(true);
      const res = otpProvider === "sms"
        ? await API.post("/verify-otp", { phone, otp, mode: tab, name, referral })
        : await API.post("/verify-firebase-otp", {
            idToken: await verifyFirebasePhoneOtp(firebaseOtp, otp),
            mode: tab,
            name,
            referral
          });
      if (res.data.user) {
        localStorage.setItem("mk_user_session_active", "1");
        localStorage.setItem("user", JSON.stringify(res.data.user));
        navigate("/dashboard", { replace: true });
        return;
      }
      showNotice(res.data.message || "Login failed");
    } catch (err) {
      showNotice(getErrorMessage(err, "OTP failed"));
    } finally {
      setActionLoading(false);
    }
  };

  const loginWithCode = async () => {
    if (actionLoading) return;
    const cleanPhone = toTenDigitPhone(phone);
    const cleanCode = String(loginCode || "").replace(/\D/g, "").slice(0, 6);

    if (cleanPhone.length !== 10 || cleanCode.length !== 6) {
      showNotice("Enter mobile number and 6 digit login code");
      return;
    }

    setActionLoading(true);
    try {
      const res = await API.post("/login-with-code", { phone: cleanPhone, code: cleanCode });
      if (res.data.user) {
        localStorage.setItem("mk_user_session_active", "1");
        localStorage.setItem("user", JSON.stringify(res.data.user));
        navigate("/dashboard", { replace: true });
        return;
      }
      showNotice(res.data.message || "Login failed");
    } catch (err) {
      showNotice(getErrorMessage(err, "Login code failed"));
    } finally {
      setActionLoading(false);
    }
  };

  const isPhoneValid = phone.length === 10;
  const isRegister = tab === "register";
  const isStepOneValid = isRegister ? isPhoneValid && String(name || "").trim().length > 0 : isPhoneValid;
  const expectedOtpLength = 6;
  const isStepTwoValid = otp.length === expectedOtpLength;
  const otpExpired = step === 2 && otpExpiresAt > 0 && otpSecondsLeft <= 0;
  const isActionEnabled = !actionLoading && (step === 1 ? isStepOneValid : isStepTwoValid && !otpExpired);

  return (
    <div style={page}>
      <div style={topRow}>
        {!inAppWebView && (
          <button type="button" onClick={() => navigate("/", { replace: true })} style={backButton}>
            <ArrowLeft size={18} />
          </button>
        )}
        <BrandLogo />
      </div>

      {!!notice && <div style={noticeBar}>{notice}</div>}

      <section style={heroSection}>
        <div style={heroAccent} />
        <h1 style={heroTitle}>{isRegister ? "Create account" : "Welcome Back!"}</h1>
        <p style={heroSubtitle}>{isRegister ? "Sign up in seconds" : "Enter your phone number to continue"}</p>
      </section>

      <section style={card}>
        <div style={tabWrap}>
          <button type="button" onClick={() => resetFlow("login")} style={{ ...tabButton, ...(tab === "login" ? activeTabButton : {}) }}>
            LOGIN
          </button>
          <button type="button" onClick={() => resetFlow("register")} style={{ ...tabButton, ...(tab === "register" ? activeTabButton : {}) }}>
            REGISTER
          </button>
        </div>

        {isRegister && (
          <>
            <label style={fieldLabel}>FULL NAME</label>
            <div style={inputShell}>
              <span style={leftIcon}><UserIcon size={18} /></span>
              <input
                placeholder="Enter your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={textInputWithIcon}
              />
            </div>
          </>
        )}

        <label style={fieldLabel}>MOBILE NUMBER</label>
        <div style={phoneInputShell}>
          <span style={countryCode}>+91</span>
          <input
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            placeholder="Enter 10 digit number"
            value={phone}
            onChange={(e) => setPhone(toTenDigitPhone(e.target.value))}
            style={phoneInput}
          />
          {isPhoneValid && <span style={validMark}><Check size={18} /></span>}
        </div>

        {isRegister && (
          <>
            <label style={fieldLabel}>REFERRAL CODE <span style={optionalText}>(Optional)</span></label>
            <div style={inputShell}>
              <span style={leftIcon}><Gift size={18} /></span>
              <input
                placeholder="ENTER CODE"
                value={referral}
                onChange={(e) => setReferral(e.target.value)}
                style={textInputWithIcon}
              />
            </div>
          </>
        )}

        {step === 2 && (
          <div style={otpPanel}>
            <div style={otpPanelTitle}>ENTER OTP</div>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={expectedOtpLength}
              placeholder="----"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, expectedOtpLength))}
              style={otpInput}
            />
            <div style={otpHelp}>
              {otpExpired ? "OTP expired. Send a new OTP." : `OTP expires in ${otpSecondsLeft || OTP_EXPIRY_SECONDS} seconds.`}
            </div>
            {otpExpired && (
              <button type="button" style={resendButton} onClick={sendOtp} disabled={!isStepOneValid || actionLoading}>
                {actionLoading ? "Sending..." : "Send OTP Again"}
              </button>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={step === 1 ? sendOtp : verifyOtp}
          style={isActionEnabled ? ctaButton : ctaButtonDisabled}
          disabled={!isActionEnabled}
        >
          {actionLoading ? (step === 1 ? "Sending OTP..." : "Verifying...") : step === 1 ? "Get OTP" : "Verify OTP"}
        </button>

        {!isRegister && step === 1 && (
          <div style={codeHelpWrap}>
            {!showCodeLogin ? (
              <button type="button" style={codeHelpButton} onClick={() => setShowCodeLogin(true)}>
                Need help with login?
              </button>
            ) : (
              <div style={codeLoginPanel}>
                <div style={codeLoginHeader}>
                  <span style={codeLoginText}>Use admin code if OTP is not coming.</span>
                  <button type="button" style={codeCloseButton} onClick={() => { setShowCodeLogin(false); setLoginCode(""); }}>
                    Hide
                  </button>
                </div>
                <div style={codeInputRow}>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="6 digit code"
                    value={loginCode}
                    onChange={(e) => setLoginCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    style={codeInput}
                  />
                  <button
                    type="button"
                    onClick={loginWithCode}
                    disabled={actionLoading || !isPhoneValid || loginCode.length !== 6}
                    style={actionLoading || !isPhoneValid || loginCode.length !== 6 ? codeLoginButtonDisabled : codeLoginButton}
                  >
                    Login
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      <div style={policyText}>
        By continuing, you agree to our{" "}
        <button type="button" style={policyLink} onClick={() => navigate("/terms-and-conditions")}>Terms of Service</button>
        {" "}and{" "}
        <button type="button" style={policyLink} onClick={() => navigate("/privacy-policy")}>Privacy Policy</button>
      </div>
    </div>
  );
}

const page = {
  minHeight: "100vh",
  background: "linear-gradient(180deg,#f9fbff 0%,#f4f6fb 50%,#ffffff 100%)",
  padding: "14px 15px 46px",
  display: "grid",
  alignContent: "start",
  gridTemplateColumns: "minmax(0, 430px)",
  justifyContent: "center",
  boxSizing: "border-box",
  overflowX: "hidden"
};

const policyLink = {
  border: 0,
  background: "transparent",
  color: "#2563eb",
  padding: 0,
  font: "inherit",
  fontWeight: 800,
  textDecoration: "underline"
};

const topRow = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

const backButton = {
  width: "36px",
  height: "36px",
  borderRadius: "10px",
  border: "1px solid #dbe4f0",
  background: "#fff",
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
  flexShrink: 0
};

const noticeBar = {
  marginTop: "14px",
  background: "linear-gradient(90deg,#fff7ed,#fffbeb)",
  border: "1px solid #fdba74",
  color: "#c2410c",
  borderRadius: "14px",
  padding: "12px 14px",
  fontWeight: 700,
  boxShadow: "0 8px 18px rgba(234,88,12,0.08)"
};

const heroSection = {
  marginTop: "16px",
  display: "grid",
  gap: "6px",
  paddingLeft: "14px",
  position: "relative"
};

const heroAccent = {
  position: "absolute",
  left: 0,
  top: 0,
  width: "4px",
  height: "40px",
  borderRadius: "999px",
  background: "linear-gradient(180deg,#2563eb,#4f46e5)"
};

const heroTitle = {
  margin: 0,
  fontSize: "22px",
  fontWeight: 900,
  color: "#0f172a"
};

const heroSubtitle = {
  margin: 0,
  color: "#64748b",
  fontSize: "13px"
};

const card = {
  background: "#fff",
  marginTop: "14px",
  padding: "13px",
  borderRadius: "22px",
  border: "1px solid #edf2f7",
  boxShadow: "0 18px 36px rgba(15,23,42,0.06)",
  display: "grid",
  gap: "12px"
};

const tabWrap = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "8px",
  background: "#f1f5f9",
  borderRadius: "18px",
  padding: "4px"
};

const tabButton = {
  height: "42px",
  border: 0,
  borderRadius: "14px",
  background: "transparent",
  color: "#64748b",
  fontWeight: 800,
  fontSize: "15px"
};

const activeTabButton = {
  background: "linear-gradient(180deg,#2b67ff,#0f4fe3)",
  color: "#fff",
  boxShadow: "0 12px 24px rgba(37,99,235,0.28)"
};

const fieldLabel = {
  color: "#64748b",
  fontSize: "12px",
  fontWeight: 800,
  letterSpacing: "0.08em",
  marginTop: "2px"
};

const optionalText = {
  letterSpacing: "normal",
  textTransform: "none",
  fontWeight: 600,
  color: "#94a3b8"
};

const inputShell = {
  position: "relative"
};

const leftIcon = {
  position: "absolute",
  left: "16px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "#94a3b8"
};

const textInputWithIcon = {
  width: "100%",
  height: "52px",
  borderRadius: "18px",
  border: "1px solid #dbe4f0",
  padding: "0 16px 0 48px",
  boxSizing: "border-box",
  fontSize: "18px",
  color: "#0f172a",
  background: "#fff"
};

const phoneInputShell = {
  height: "52px",
  borderRadius: "18px",
  border: "1px solid #dbe4f0",
  background: "#fff",
  display: "grid",
  gridTemplateColumns: "56px 1fr 34px",
  alignItems: "center",
  overflow: "hidden"
};

const countryCode = {
  height: "100%",
  display: "grid",
  placeItems: "center",
  borderRight: "1px solid #e5e7eb",
  fontWeight: 800,
  color: "#334155",
  background: "#fafcff"
};

const phoneInput = {
  width: "100%",
  height: "100%",
  border: 0,
  outline: 0,
  padding: "0 12px",
  fontSize: "20px",
  color: "#0f172a"
};

const validMark = {
  color: "#22c55e",
  display: "grid",
  placeItems: "center"
};

const codeHelpWrap = {
  display: "grid",
  justifyItems: "center",
  marginTop: "-2px"
};

const codeHelpButton = {
  border: 0,
  background: "transparent",
  color: "#64748b",
  fontSize: "12px",
  fontWeight: 800,
  textDecoration: "underline",
  padding: "4px 8px"
};

const codeLoginPanel = {
  width: "100%",
  border: "1px solid #fed7aa",
  borderRadius: "18px",
  background: "linear-gradient(135deg,#fff7ed,#fffbeb)",
  padding: "10px",
  display: "grid",
  gap: "8px"
};

const codeLoginHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "10px"
};

const codeLoginText = {
  color: "#9a3412",
  fontSize: "12px",
  fontWeight: 800,
  lineHeight: 1.35
};

const codeCloseButton = {
  border: 0,
  background: "transparent",
  color: "#c2410c",
  fontSize: "11px",
  fontWeight: 900,
  padding: "2px 4px"
};

const codeInputRow = {
  display: "grid",
  gridTemplateColumns: "1fr 92px",
  gap: "8px"
};

const codeInput = {
  minWidth: 0,
  height: "42px",
  borderRadius: "14px",
  border: "1px solid #fdba74",
  background: "#fff",
  color: "#7c2d12",
  fontSize: "18px",
  fontWeight: 900,
  letterSpacing: "0.16em",
  padding: "0 12px",
  outline: 0,
  boxSizing: "border-box"
};

const codeLoginButton = {
  height: "42px",
  border: 0,
  borderRadius: "14px",
  background: "linear-gradient(180deg,#f59e0b,#ea580c)",
  color: "#fff",
  fontSize: "12px",
  fontWeight: 900,
  boxShadow: "0 10px 20px rgba(234,88,12,0.18)"
};

const codeLoginButtonDisabled = {
  ...codeLoginButton,
  background: "#fed7aa",
  color: "#fff7ed",
  boxShadow: "none",
  cursor: "not-allowed"
};

const otpPanel = {
  marginTop: "2px",
  borderRadius: "18px",
  border: "1px solid #cfe0ff",
  background: "linear-gradient(180deg,#f8fbff,#fdfefe)",
  padding: "14px",
  display: "grid",
  gap: "10px"
};

const otpPanelTitle = {
  color: "#1e3a8a",
  fontSize: "12px",
  fontWeight: 900,
  letterSpacing: "0.08em"
};

const otpInput = {
  width: "100%",
  height: "58px",
  borderRadius: "16px",
  border: "2px solid #93c5fd",
  background: "#fff",
  textAlign: "center",
  fontSize: "30px",
  fontWeight: 900,
  letterSpacing: "0.45em",
  color: "#1e3a8a",
  paddingLeft: "0.45em",
  boxSizing: "border-box"
};

const otpHelp = {
  fontSize: "12px",
  color: "#64748b"
};

const resendButton = {
  height: "40px",
  border: "1px solid #bfdbfe",
  borderRadius: "14px",
  background: "#eff6ff",
  color: "#1d4ed8",
  fontSize: "13px",
  fontWeight: 900
};

const ctaButton = {
  width: "100%",
  height: "52px",
  borderRadius: "18px",
  border: 0,
  background: "linear-gradient(180deg,#2d6bff,#1552e8)",
  color: "#fff",
  fontSize: "18px",
  fontWeight: 900,
  boxShadow: "0 18px 30px rgba(37,99,235,0.24)"
};
const ctaButtonDisabled = {
  ...ctaButton,
  background: "linear-gradient(180deg,#c8d4f8,#b4c1e8)",
  color: "#f8fbff",
  boxShadow: "none",
  cursor: "not-allowed"
};

const policyText = {
  position: "fixed",
  left: "50%",
  bottom: "12px",
  transform: "translateX(-50%)",
  width: "min(430px, calc(100vw - 30px))",
  textAlign: "center",
  color: "#94a3b8",
  fontSize: "12px",
  lineHeight: 1.45,
  pointerEvents: "none"
};

export default Login;
